/**
 * Single product: gallery thumbnails, hover zoom, vanilla tabs.
 */
const SELECTORS = {
    page: '[data-product-page]',
    gallery: '[data-product-gallery]',
    galleryMain: '[data-product-gallery-main]',
    galleryStage: '[data-product-gallery-stage]',
    galleryThumb: '.product-gallery__thumb',
    galleryTemplate: '[data-product-gallery-slide-template]',
    tabsWrapper: '.product-page__tabs .woocommerce-tabs',
    tabLink: '.tabs li a',
    tabPanel: '.woocommerce-Tabs-panel',
};
const DESKTOP_ZOOM_QUERY = '(min-width: 1024px) and (hover: hover) and (pointer: fine)';
export function initProductPage() {
    const page = document.querySelector(SELECTORS.page);
    if (!page) {
        return;
    }
    initGallery(page);
    initTabs(page);
}
function initGallery(page) {
    const gallery = page.querySelector(SELECTORS.gallery);
    if (!gallery) {
        return;
    }
    const main = gallery.querySelector(SELECTORS.galleryMain);
    const stage = gallery.querySelector(SELECTORS.galleryStage);
    const thumbs = gallery.querySelectorAll(SELECTORS.galleryThumb);
    const template = gallery.querySelector(SELECTORS.galleryTemplate);
    if (!main || thumbs.length === 0) {
        bindGalleryZoom(stage, main);
        return;
    }
    thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
            swapGalleryImage(main, thumb, thumbs, template);
        });
    });
    bindGalleryZoom(stage, main);
}
function swapGalleryImage(main, activeThumb, thumbs, template) {
    const fullSrc = activeThumb.dataset.fullSrc;
    if (!fullSrc) {
        return;
    }
    main.classList.add('is-changing');
    window.setTimeout(() => {
        const slideIndex = activeThumb.dataset.galleryIndex ?? '0';
        let html = '';
        if (template?.content) {
            const slideNodes = template.content.querySelectorAll('[data-gallery-slide]');
            const slideNode = slideNodes[Number(slideIndex)];
            if (slideNode) {
                html = slideNode.innerHTML;
            }
        }
        if (html) {
            main.innerHTML = html;
        }
        else {
            const img = main.querySelector('img');
            if (img) {
                img.src = fullSrc;
                img.removeAttribute('srcset');
                img.removeAttribute('sizes');
            }
        }
        thumbs.forEach((thumb) => {
            const isActive = thumb === activeThumb;
            thumb.classList.toggle('is-active', isActive);
            thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        main.classList.remove('is-changing');
    }, 180);
}
function bindGalleryZoom(stage, main) {
    if (!stage || !main) {
        return;
    }
    const media = window.matchMedia(DESKTOP_ZOOM_QUERY);
    const onMove = (event) => {
        if (!media.matches) {
            return;
        }
        const img = main.querySelector('img');
        if (!img) {
            return;
        }
        const rect = stage.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        main.classList.add('is-zoomed');
    };
    const onLeave = () => {
        const img = main.querySelector('img');
        main.classList.remove('is-zoomed');
        if (img) {
            img.style.transformOrigin = 'center center';
            img.style.transform = '';
        }
    };
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', onLeave);
    const onMediaChange = () => {
        if (!media.matches) {
            onLeave();
        }
    };
    media.addEventListener('change', onMediaChange);
}
function initTabs(page) {
    const wrapper = page.closest('.product-page')?.querySelector(SELECTORS.tabsWrapper)
        ?? document.querySelector(SELECTORS.tabsWrapper);
    if (!wrapper) {
        return;
    }
    const tabLinks = wrapper.querySelectorAll(SELECTORS.tabLink);
    const panels = wrapper.querySelectorAll(SELECTORS.tabPanel);
    if (tabLinks.length === 0 || panels.length === 0) {
        return;
    }
    const activateTab = (link) => {
        const targetId = link.getAttribute('href')?.replace('#', '');
        if (!targetId) {
            return;
        }
        tabLinks.forEach((item) => {
            const li = item.parentElement;
            const isActive = item === link;
            li?.classList.toggle('active', isActive);
            item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        panels.forEach((panel) => {
            const isActive = panel.id === targetId;
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;
        });
    };
    tabLinks.forEach((link, index) => {
        link.setAttribute('role', 'tab');
        link.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        link.addEventListener('click', (event) => {
            event.preventDefault();
            activateTab(link);
        });
    });
    panels.forEach((panel, index) => {
        panel.hidden = index !== 0;
        panel.classList.toggle('is-active', index === 0);
    });
    const firstLink = tabLinks[0];
    if (firstLink) {
        firstLink.parentElement?.classList.add('active');
    }
    const tabList = wrapper.querySelector('.tabs');
    if (tabList) {
        tabList.setAttribute('role', 'tablist');
    }
    tabLinks.forEach((link) => {
        link.addEventListener('keydown', (event) => {
            const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
            if (!keys.includes(event.key)) {
                return;
            }
            event.preventDefault();
            const links = Array.from(tabLinks);
            const currentIndex = links.indexOf(link);
            let nextIndex = currentIndex;
            if ('ArrowLeft' === event.key) {
                nextIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
            }
            else if ('ArrowRight' === event.key) {
                nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
            }
            else if ('Home' === event.key) {
                nextIndex = 0;
            }
            else if ('End' === event.key) {
                nextIndex = links.length - 1;
            }
            links[nextIndex]?.focus();
            activateTab(links[nextIndex]);
        });
    });
}
